import type {
  CanonicalCompatibilityEvidence,
} from '../tree/CanonicalTreeCompatibilityEdgeProjection';

import {
  TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_MILESTONE,
  deriveTreeRuntimeMissingParentReferencePairs,
  type TreeRuntimeMissingParentIdentityParityDecision,
} from './TreeRuntimeMissingParentIdentityParityAuthority';

export const TREE_RUNTIME_MISSING_PARENT_REPAIR_CANDIDATE_PLANNING_MILESTONE =
  'CC-TREE-LIVE-003G2T' as const;

export type TreeRuntimeMissingParentRepairCandidatePlanningStatus =
  | 'BLOCKED_NOT_ELIGIBLE'
  | 'BLOCKED_INVALID_PRIVATE_EVIDENCE'
  | 'BLOCKED_PARITY_CONTRADICTION'
  | 'PLANNED_RESTORE_CANDIDATES';

export interface TreeRuntimeMissingParentRestoreCandidate {
  readonly parentId: string;
  readonly childIds: readonly string[];
  readonly action: 'RESTORE_SOFT_DELETED_PARENT';
}

export interface TreeRuntimeMissingParentRepairCandidatePlan {
  readonly milestone:
    typeof TREE_RUNTIME_MISSING_PARENT_REPAIR_CANDIDATE_PLANNING_MILESTONE;

  readonly status:
    TreeRuntimeMissingParentRepairCandidatePlanningStatus;

  readonly candidateCount: number;
  readonly referencePairCount: number;

  readonly candidates:
    readonly TreeRuntimeMissingParentRestoreCandidate[];

  readonly containsPrivateIdentity: true;
  readonly publishable: false;
  readonly readOnly: true;
  readonly repairExecuted: false;
  readonly repairReady: false;
}

const blocked = (
  status:
    Exclude<
      TreeRuntimeMissingParentRepairCandidatePlanningStatus,
      'PLANNED_RESTORE_CANDIDATES'
    >,
): TreeRuntimeMissingParentRepairCandidatePlan =>
  Object.freeze({
    milestone:
      TREE_RUNTIME_MISSING_PARENT_REPAIR_CANDIDATE_PLANNING_MILESTONE,

    status,

    candidateCount: 0,
    referencePairCount: 0,

    candidates:
      Object.freeze([]),

    containsPrivateIdentity: true,
    publishable: false,
    readOnly: true,
    repairExecuted: false,
    repairReady: false,
  });

export function planTreeRuntimeMissingParentRepairCandidates(
  input: {
    readonly evidence:
      readonly CanonicalCompatibilityEvidence[];

    readonly decision:
      TreeRuntimeMissingParentIdentityParityDecision;
  },
): TreeRuntimeMissingParentRepairCandidatePlan {
  const decision =
    input.decision;

  if (
    decision.milestone !==
      TREE_RUNTIME_MISSING_PARENT_IDENTITY_PARITY_MILESTONE ||
    decision.status !==
      'IDENTITY_PARITY_PROVEN' ||
    decision.readOnly !== true ||
    decision.repairExecuted !== false ||
    decision.repairReady !== false
  ) {
    return blocked(
      'BLOCKED_PARITY_CONTRADICTION',
    );
  }

  if (
    decision.identityParityProven !== true ||
    decision.repairPlanningEligible !== true ||
    decision.repairReadinessStatus !==
      'ELIGIBLE_FOR_REPAIR_DESIGN'
  ) {
    return blocked(
      'BLOCKED_NOT_ELIGIBLE',
    );
  }

  const lifecycle =
    decision.missingParentReferenceLifecycle;

  if (
    lifecycle === null ||
    decision.serverMissingReferencePairsCount ===
      null ||
    lifecycle
      .missingParentReferenceActiveSameGraphCount !== 0 ||
    lifecycle
      .missingParentReferenceCrossGraphCount !== 0 ||
    lifecycle
      .missingParentReferencePhysicallyAbsentCount !== 0 ||
    lifecycle
      .missingParentReferenceIndeterminateCount !== 0 ||
    lifecycle
      .missingParentReferenceSoftDeletedSameGraphCount !==
        decision.serverMissingReferencePairsCount
  ) {
    return blocked(
      'BLOCKED_PARITY_CONTRADICTION',
    );
  }

  const pairs =
    deriveTreeRuntimeMissingParentReferencePairs(
      input.evidence,
    );

  if (pairs === null) {
    return blocked(
      'BLOCKED_INVALID_PRIVATE_EVIDENCE',
    );
  }

  if (
    pairs.length === 0 ||
    pairs.length !==
      decision.browserMissingReferencePairsCount ||
    pairs.length !==
      decision.serverMissingReferencePairsCount
  ) {
    return blocked(
      'BLOCKED_PARITY_CONTRADICTION',
    );
  }

  const childrenByParent =
    new Map<string, Set<string>>();

  for (const pair of pairs) {
    const childIds =
      childrenByParent.get(
        pair.parentId,
      ) ??
      new Set<string>();

    childIds.add(
      pair.childId,
    );

    childrenByParent.set(
      pair.parentId,
      childIds,
    );
  }

  const candidates =
    [...childrenByParent.entries()]
      .sort(([left], [right]) => {
        if (left < right) return -1;
        if (left > right) return 1;
        return 0;
      })
      .map(
        ([parentId, childIds]) =>
          Object.freeze({
            parentId,

            childIds:
              Object.freeze(
                [...childIds].sort(),
              ),

            action:
              'RESTORE_SOFT_DELETED_PARENT' as const,
          }),
      );

  return Object.freeze({
    milestone:
      TREE_RUNTIME_MISSING_PARENT_REPAIR_CANDIDATE_PLANNING_MILESTONE,

    status:
      'PLANNED_RESTORE_CANDIDATES',

    candidateCount:
      candidates.length,

    referencePairCount:
      pairs.length,

    candidates:
      Object.freeze(candidates),

    containsPrivateIdentity: true,
    publishable: false,
    readOnly: true,
    repairExecuted: false,
    repairReady: false,
  });
}
